import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { profile, SECTIONS } from '../../content/profile.js';
import { LocaleSelect } from './LocaleSelect.js';

/**
 * Sticky header.
 *
 * It starts transparent over the hero and takes a solid background once the
 * hero has scrolled past, so the links stay legible against the page without
 * a permanent bar sitting over the photograph.
 *
 * The state comes from an IntersectionObserver on a sentinel rather than a
 * scroll listener, so nothing runs on the main thread per scroll frame.
 */
export function Header({ sentinel }: { sentinel: React.RefObject<HTMLDivElement | null> }) {
  const { t } = useTranslation(['common']);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const drawerId = useId();
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const target = sentinel.current;
    if (!target) return;
    const observer = new IntersectionObserver(([entry]) => setPinned(!entry?.isIntersecting), {
      threshold: 0,
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [sentinel]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const links = SECTIONS.filter((section) => section !== 'hire');

  return (
    <header ref={headerRef} className={`header${pinned ? ' header--pinned' : ''}`}>
      <a className="header__mark" href="#top">
        {profile.initials}
      </a>

      <nav className="header__nav" aria-label={t('common:nav.menu')}>
        {links.map((section) => (
          <a key={section} className="header__link" href={`#${section}`}>
            {t(`common:nav.${section}`)}
          </a>
        ))}
        <LocaleSelect variant="header" />
        <a className="header__cta" href="#hire">
          {t('common:nav.hire')}
        </a>
      </nav>

      <div className="header__compact">
        <a className="header__cta" href="#hire">
          {t('common:nav.hire')}
        </a>
        <button
          type="button"
          className="header__toggle"
          aria-expanded={open}
          aria-controls={drawerId}
          aria-label={t('common:nav.menu')}
          onClick={() => setOpen((value) => !value)}
        >
          <span />
          <span />
        </button>
      </div>

      {open && (
        <nav id={drawerId} className="header__drawer" aria-label={t('common:nav.menu')}>
          {links.map((section) => (
            <a key={section} href={`#${section}`} onClick={() => setOpen(false)}>
              {t(`common:nav.${section}`)}
            </a>
          ))}
          <div className="header__drawer-locale">
            <LocaleSelect variant="header" />
          </div>
        </nav>
      )}
    </header>
  );
}
